import React, { useState, useEffect, RefObject, useRef } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { dynamicTranslate } from 'components/Translation';
import { BodyM, BodyMBold, BodyS, BodySBold } from 'components/Typography/Typography';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import style from 'assets/jss/material-dashboard-pro-react/views/services/FixedBusinessBroadband/selectPlanDialogStyle';
import { TableCell, TableRow, makeStyles } from '@material-ui/core';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import numeral from 'numeral';
import { cloneDeep } from 'lodash';
import InfoClause from 'components/infoClause/infoClause';
import CustomTableComponent from './CustomTableComponent';
import {
	Display_PRICE,
	Offer_PRICE,
	additionalServiceTableColumnList,
	attributes,
} from 'components/ServicesTables/FBBTable/constants';
import { formatPrice } from 'utils/price';
import { useSelector } from 'react-redux';
import { getCommonLabels, getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';
import { fiberPro2b } from 'utils/killswitch';
import VatIcon from 'assets/img/icon_vat_grey.svg';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { translate } from 'components/Translation';

interface ISelectPlanDialogProps {
	hideDialog: () => void;
	isFbb: boolean;
	TableComponent: React.ElementType;
	planTitle: string;
	handleAddPlan: (
		plan: types.services.fixed.IPlan,
		additionalServices: types.services.fixed.IAdditionalService[],
		services: {
			selectedStaticIP?: {
				modes: types.ecommerce.CatalogEntryView | undefined;
				configs: types.ecommerce.CatalogEntryView | undefined;
			};
			requiredService?: types.services.fixed.IRequiredService;
		}
	) => void;
	line?: types.services.fixed.ILine;
}

// @ts-ignore
const useStyles = makeStyles(style);
// this class is used in build your plan screen which is fetching  static ip, additional services
// required services

const SelectPlanDialog = (props: ISelectPlanDialogProps) => {
	// this component get focussed - for accessibility
	const ref: RefObject<HTMLDivElement> = useRef(null);
	const fiberProLabels = useSelector(getFiberProLabels);
	const commonLabels = useSelector(getCommonLabels);
	useEffect(() => {
		if (ref.current) ref.current.focus();
	}, []);
	const { hideDialog, handleAddPlan, line, TableComponent, planTitle } = props;
	const [requiredService, setRequiredService] = useState<types.services.fixed.IRequiredService | undefined>(
		line?.requiredService
	);
	const [selectedAdditionalServices, setSelectedAdditionalServices] = useState<
		types.services.fixed.IAdditionalService[]
	>(line?.additionalServices || []);
	const [selectedPlan, setSelectedPlan] = useState<types.services.fixed.IPlan | null>(line?.plan || null);
	const [displayStaticIP, setDisplayStaticIp] = useState(false || !!line?.staticIP || !!line?.plan);
	const [checkedStaticIP, setCheckedStaticIP] = useState(false || !!line?.staticIP?.mainProduct);
	const [loadingPlans, setLoadingPlans] = useState(false);
	const [staticIpMsg, setStaticIpMsg] = useState('');
	const [selectedStaticIP, setSelectedStaticIP] = useState<{
		modes: types.ecommerce.CatalogEntryView | undefined;
		configs: types.ecommerce.CatalogEntryView | undefined;
		mainProduct?: types.ecommerce.CatalogEntryView | undefined;
	}>({ modes: line?.staticIP?.modes, configs: line?.staticIP?.configs, mainProduct: line?.staticIP?.mainProduct });
	const [staticIPOptions, setStaticIPOptions] = useState<{
		modes: types.ecommerce.CatalogEntryView[] | [];
		configs: types.ecommerce.CatalogEntryView[] | [];
		mainProduct: types.ecommerce.CatalogEntryView | undefined;
	}>({ modes: [], configs: [], mainProduct: undefined });
	const [additionalServicesAvailable, setAdditionalServiceAvailable] = useState<boolean>(
		!!line && line?.additionalServices?.length > 0 ? true : false
	);
	const classes = useStyles();

	const [requiredServices, setRequiredServices] = useState<types.services.fixed.IRequiredService[]>([]);
	const [additionalServices, setAdditionalServices] = useState<types.services.fixed.IAdditionalService[]>([]);
	const remoteConfig = useSelector(remoteConfigSelector);
	const isFiberPro2b = fiberPro2b(remoteConfig); // kill switch to show fiber pro 2b changes
	useEffect(() => {
		// Fetching static ip options
		ECommerceService.getGroupContent(idConfig.FBB.STATIC_IP[env]).subscribe(({ response }: any) => {
			if (response.catalogEntryView) {
				let staticOptions: {
					modes: types.ecommerce.CatalogEntryView[] | [];
					configs: types.ecommerce.CatalogEntryView[] | [];
					mainProduct: types.ecommerce.CatalogEntryView | undefined;
				} = {
					mainProduct: undefined,
					modes: [],
					configs: [],
				};
				let modes = response.catalogEntryView.filter(
					(staticIpOption: types.ecommerce.CatalogEntryView) =>
						extractAttributeValue(staticIpOption, CustomAttribute.IP_MODE) === 'TRUE'
				);
				let configs = response.catalogEntryView.filter(
					(staticIpOption: types.ecommerce.CatalogEntryView) =>
						extractAttributeValue(staticIpOption, CustomAttribute.IP_CONFIG) === 'TRUE'
				);
				let mainProduct: types.ecommerce.CatalogEntryView[] | undefined = response.catalogEntryView.filter(
					(staticIpOption: types.ecommerce.CatalogEntryView) =>
						extractAttributeValue(staticIpOption, CustomAttribute.MAIN_STATIC_IP_CONFIG) === 'TRUE'
				);
				staticOptions.modes = modes;
				staticOptions.configs = configs;
				staticOptions.mainProduct = mainProduct ? mainProduct[0] : undefined;
				setStaticIPOptions(staticOptions);
			}
		});
	}, []);

	useEffect(() => {
		// Fetching required services
		if (!props.isFbb) {
			ECommerceService.getGroupContent(idConfig.FBB.REQUIRED_SERVICES[env]).subscribe(({ response }) => {
				setRequiredServices(
					response.catalogEntryView.map((entry: types.ecommerce.CatalogEntryView) => ({
						name: entry.name,
						uniqueID: entry.uniqueID,
						partNumber: entry.partNumber,
					}))
				);
				setRequiredService(response.catalogEntryView[0]);
			});
		}
	}, [props.isFbb]);

	useEffect(() => {
		// Fetching additional services

		ECommerceService.getGroupContent(idConfig.FBB.ADDITIONAL_SERVICES[env]).subscribe(
			({ response }: { response: { catalogEntryView: types.ecommerce.CatalogEntryView[] } }) => {
				if (response.catalogEntryView?.length) {
					setAdditionalServiceAvailable(true);
				}
				const filteredAdditionalServices = response.catalogEntryView.filter(
					(service) =>
						extractAttributeValue(service, CustomAttribute.MONTHLY_FEE) &&
						extractAttributeValue(service, CustomAttribute.INSTALLATION_FEE)
				);
				setAdditionalServices(
					filteredAdditionalServices.map((service) => ({
						uniqueID: service.uniqueID,
						name: service.name,
						monthlyFee: extractAttributeValue(service, CustomAttribute.MONTHLY_FEE),
						oneTimeFee: extractAttributeValue(service, CustomAttribute.INSTALLATION_FEE),
						partNumber: service.partNumber,
					}))
				);
			}
		);
	}, []);
	// Checking additional service is selected
	const checkService = (service: any) => {
		setSelectedAdditionalServices((selectedAdditionalServices) => {
			const isServiceChecked = selectedAdditionalServices.find(({ uniqueID }) => service.uniqueID === uniqueID);

			if (isServiceChecked)
				return selectedAdditionalServices.filter(({ uniqueID }) => service.uniqueID !== uniqueID);
			else
				return [
					...selectedAdditionalServices,
					{
						...cloneDeep(service),
					},
				];
		});
	};
	// Making the columns of additional services table
	const additionalTableColumn = additionalServiceTableColumnList.map((column: string) => {
		return (
			<TableCell>
				<BodySBold customClassName={classes.colorWhite}>{column ? fiberProLabels[column] : ''}</BodySBold>
			</TableCell>
		);
	});
	// Making the rows of additional services table
	const additionalServiceTableRows = additionalServices.map((service) => {
		const isChecked = !!selectedAdditionalServices.find(({ uniqueID }) => uniqueID === service.uniqueID);
		return (
			<TableRow
				key={service.uniqueID}
				onClick={() => checkService(service)}
				className={isChecked ? classes.selectedRow : classes.unSelectedRow}
			>
				<TableCell align="right">
					<CustomCheckbox
						checked={isChecked}
						onClick={() => {}}
						formControlProps={{ 'aria-label': `Additional Service -  ${service.name}` }}
					/>
				</TableCell>
				<TableCell>
					<BodyS>{service.name}</BodyS>
				</TableCell>
				<TableCell>
					<BodyS>{dynamicTranslate(commonLabels.price, formatPrice(service.monthlyFee))}</BodyS>
				</TableCell>
				<TableCell>
					<BodyS>{dynamicTranslate(commonLabels.price, formatPrice(service.oneTimeFee))}</BodyS>
				</TableCell>
			</TableRow>
		);
	});
	//  get price of static ip
	const getStaticIpPrice = (prices: any) => {
		const staticIpPrice = {
			offer: prices.find((singlePrice: any) => singlePrice.usage === Offer_PRICE),
			display: prices.find((singlePrice: any) => singlePrice.usage === Display_PRICE),
		};
		return numeral(staticIpPrice.display.value).format('0.00');
	};
	return (
		<CustomDialog
			isOpen
			width={938}
			hideDialog={() => hideDialog()}
			header={fiberProLabels.buildPlan}
			rightSideButtons={[
				{
					text: fiberProLabels.buildPlan,

					disabled: !selectedPlan || (displayStaticIP && checkedStaticIP && !selectedStaticIP),
					color: 'primary-light',
					onClick: () => {
						if (!!selectedPlan)
							handleAddPlan(selectedPlan, [...selectedAdditionalServices], {
								selectedStaticIP,
								requiredService,
							});
					},
				},
			]}
			dialogBody={
				<div className={classes.dialogBodyWrapper} ref={ref}>
					{/* this is FBBTable component which is getting passed as props */}
					<TableComponent
						selectedTab={selectedPlan?.uniqueID || null} // will give current selected table row
						setSelectedTab={(plan: types.ecommerce.CatalogEntryView | null) => {
							// this function is selecting the plans while clicking on table row
							const haveStaticIP =
								extractAttributeValue(plan, CustomAttribute.STATIC_IP, 'FALSE')?.toLowerCase() ===
								'true';

							// it will display static ip option when plan row is selected
							if (!haveStaticIP) {
								setSelectedStaticIP({ modes: undefined, configs: undefined, mainProduct: undefined });
								setCheckedStaticIP(false);
							}
							setDisplayStaticIp(haveStaticIP);
							!!plan
								? setSelectedPlan({
										uniqueID: plan.uniqueID,
										partNumber: plan.partNumber,
										name: plan.name,
										download: extractAttributeValue(plan, CustomAttribute.HBB_DOWN_SPEED),
										contract: extractAttributeValue(plan, CustomAttribute.CONTRACT_DURATION),
										fbbPro: extractAttributeValue(plan, CustomAttribute.FBB_PRO) === 'TRUE',
										fee: plan.price[0].value,
										shortDescription: plan?.shortDescription,
										tariffPlanId: extractAttributeValue(plan, CustomAttribute.PLAN_TARIFF_ID),
										provisioningId: extractAttributeValue(plan, CustomAttribute.PROVISIONING_ID),
										wfbbProductId: extractAttributeValue(plan, CustomAttribute.WFBB),
										wfbbProductName: plan.attributes.find(
											(attribute) => attribute.identifier === CustomAttribute.WFBB
										)?.name,
								  })
								: setSelectedPlan(null);
						}}
						loadingPlans={setLoadingPlans}
					/>

					<div tabIndex={0}>
						<ConditionalRender
							show={requiredServices.length > 0 && !props.isFbb}
							onTrue={() => (
								<div className={classes.requiredServiceMainWrapper}>
									<BodyMBold color="primary-light" customClassName={classes.requiredTitle}>
										{translate('add.new.line.fbb.dialog.required')}
									</BodyMBold>
									<div className={classes.requiredServiceWrapper}>
										{requiredServices.map((service) => (
											<div key={service.uniqueID}>
												<CustomRadio
													name={service.name}
													label={service.name}
													checked={requiredService?.uniqueID === service.uniqueID}
													onChange={() => setRequiredService(service)}
												/>
											</div>
										))}
									</div>
								</div>
							)}
						/>
						<ConditionalRender
							show={displayStaticIP && props.isFbb}
							onTrue={() => (
								<div className={classes.staticIpWrapper}>
									<div className={classes.staticIpCheckboxWrapper}>
										<CustomCheckbox
											disableLabel
											onClick={() => {
												if (checkedStaticIP) {
													setSelectedStaticIP({
														modes: undefined,
														configs: undefined,
														mainProduct: undefined,
													});
												} else {
													setSelectedStaticIP({
														modes: staticIPOptions?.modes.find(
															(mode) =>
																extractAttributeValue(
																	mode,
																	CustomAttribute.DEFAULT_CONFIG
																) === 'TRUE'
														),
														configs: staticIPOptions?.configs.find(
															(mode) =>
																extractAttributeValue(
																	mode,
																	CustomAttribute.DEFAULT_CONFIG
																) === 'TRUE'
														),
														mainProduct: staticIPOptions?.mainProduct
															? staticIPOptions?.mainProduct
															: undefined,
													});
												}
												setCheckedStaticIP(!checkedStaticIP);
											}}
											checked={checkedStaticIP}
											label={fiberProLabels.staticIp}
											formControlProps={{ 'aria-label': `Static IP` }}
										/>
										<BodyMBold color="primary-light">{fiberProLabels.staticIp}</BodyMBold>
									</div>
									{selectedStaticIP && fiberProLabels.staticIpBanner && (
										<InfoClause
											message={dynamicTranslate(
												fiberProLabels.staticIpBanner,
												getStaticIpPrice(
													isFiberPro2b
														? staticIPOptions.mainProduct?.price
														: selectedStaticIP.modes?.price
												)
											)}
										/>
									)}
									{staticIpMsg && <InfoClause message={staticIpMsg} />}
									<div className={classes.staticIpOptionsWrapper}>
										{isFiberPro2b && (
											<div className={classes.staticIpOptionsConfigs}>
												<div className={classes.staticIpHeadingWrapper}>
													<BodyM>{fiberProLabels.staticIpConfig}</BodyM>

													<div className={classes.staticIpGroup}>
														{(staticIPOptions.configs as any[]).map(
															(product: types.ecommerce.CatalogEntryView, i) => (
																<div key={product.uniqueID}>
																	<CustomRadio
																		key={`Static-ip-config-${i}`}
																		onChange={() => {
																			setStaticIpMsg(
																				extractAttributeValue(
																					product,
																					CustomAttribute.WEB_PLAN_INFO
																				)
																			);
																			setSelectedStaticIP({
																				...selectedStaticIP,
																				configs: product,
																			});
																		}}
																		disabled={!checkedStaticIP}
																		name="STATIC_IP_CONFIG"
																		checked={
																			product.partNumber ===
																			selectedStaticIP?.configs?.partNumber
																		}
																		label={product.name}
																		formControlProps={{
																			'aria-label': `Static IP - ${product.name}`,
																		}}
																	/>
																</div>
															)
														)}
													</div>
												</div>
											</div>
										)}

										<div className={classes.staticIpOptionsModes}>
											<div className={classes.staticIpHeadingWrapper}>
												<BodyM>{fiberProLabels.ipMode}</BodyM>

												<div className={classes.staticIpGroup}>
													{(staticIPOptions.modes as any[]).map(
														(product: types.ecommerce.CatalogEntryView, i) => (
															<div key={product.uniqueID}>
																<CustomRadio
																	key={`Static-ip-mode-${i}`}
																	onChange={() => {
																		setSelectedStaticIP({
																			...selectedStaticIP,
																			modes: product,
																		});
																	}}
																	disabled={!checkedStaticIP}
																	name="STATIC_IP_MODE"
																	checked={
																		product.partNumber ===
																		selectedStaticIP?.modes?.partNumber
																	}
																	label={product.name}
																	formControlProps={{
																		'aria-label': `Static IP - ${product.name}`,
																	}}
																/>
															</div>
														)
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						/>
						<ConditionalRender
							show={additionalServicesAvailable}
							onTrue={() => (
								<>
									<BodyMBold color="primary-light">{fiberProLabels.selectExtService}</BodyMBold>
									<CustomTableComponent
										tableColumns={additionalTableColumn}
										tableRows={additionalServiceTableRows}
									/>
								</>
							)}
						/>
						{fiberProLabels?.vatBanner && !loadingPlans && (
							<InfoClause
								message={fiberProLabels.vatBanner}
								Icon={<img alt="vat icon" src={VatIcon} />}
							/>
						)}
					</div>
				</div>
			}
		/>
	);
};

export default SelectPlanDialog;
